import React, { useState } from 'react';
import { StatsCard } from './shared/StatsCard';
import { SegmentDropdown } from './shared/SegmentDropdown';
import { Chart } from './shared/Chart';
import { Download, DollarSign, Droplet, Fish, Wallet, AlertCircle, Bell, Mail, Repeat, Zap, Globe, Theater, BarChart, TrendingUp, Calendar, Brain } from 'lucide-react';
// User segments
const segments = [{
  id: 'all',
  label: 'All Users'
}, {
  id: 'whales',
  label: 'Whales',
  icon: <DollarSign size={16} className="text-emerald-500" data-id="element-14167" />
}, {
  id: 'dolphins',
  label: 'Dolphins',
  icon: <Droplet size={16} className="text-blue-500" data-id="element-14168" />
}, {
  id: 'minnows',
  label: 'Minnows',
  icon: <Fish size={16} className="text-cyan-500" data-id="element-14169" />
}, {
  id: 'non-spenders',
  label: 'Non-Spenders',
  icon: <Wallet size={16} className="text-gray-500" data-id="element-14170" />
}, {
  id: 'churn-risks',
  label: 'Churn Risks',
  icon: <AlertCircle size={16} className="text-red-500" data-id="element-14171" />
}];
// Campaign types
const campaignTypes = [{
  id: 'push',
  name: 'Push Notifications',
  icon: <Bell size={16} className="text-blue-500" data-id="element-14172" />,
  stats: {
    whales: {
      sent: 2500,
      opened: 1875,
      converted: 625,
      revenue: 18750
    },
    dolphins: {
      sent: 17000,
      opened: 11050,
      converted: 2550,
      revenue: 51000
    },
    minnows: {
      sent: 44000,
      opened: 22000,
      converted: 3080,
      revenue: 38500
    },
    'non-spenders': {
      sent: 316000,
      opened: 94800,
      converted: 4740,
      revenue: 23700
    },
    'churn-risks': {
      sent: 25000,
      opened: 8750,
      converted: 1250,
      revenue: 18750
    }
  },
  algorithmCoverage: {
    whales: 95,
    dolphins: 88,
    minnows: 75,
    'non-spenders': 45,
    'churn-risks': 90
  },
  fallbackMethod: {
    whales: 'lookalike',
    dolphins: 'lookalike',
    minnows: 'broad',
    'non-spenders': 'context',
    'churn-risks': 'lookalike'
  }
}, {
  id: 'email',
  name: 'Email Campaigns',
  icon: <Mail size={16} className="text-purple-500" data-id="element-14173" />,
  stats: {
    whales: {
      sent: 2500,
      opened: 1500,
      converted: 500,
      revenue: 15000
    },
    dolphins: {
      sent: 17000,
      opened: 8500,
      converted: 1700,
      revenue: 34000
    },
    minnows: {
      sent: 44000,
      opened: 17600,
      converted: 2200,
      revenue: 27500
    },
    'non-spenders': {
      sent: 316000,
      opened: 63200,
      converted: 3160,
      revenue: 15800
    },
    'churn-risks': {
      sent: 25000,
      opened: 7500,
      converted: 1000,
      revenue: 15000
    }
  },
  algorithmCoverage: {
    whales: 92,
    dolphins: 85,
    minnows: 72,
    'non-spenders': 40,
    'churn-risks': 88
  },
  fallbackMethod: {
    whales: 'lookalike',
    dolphins: 'lookalike',
    minnows: 'broad',
    'non-spenders': 'context',
    'churn-risks': 'lookalike'
  }
}, {
  id: 'retargeting',
  name: 'Retargeting Ads',
  icon: <Repeat size={16} className="text-amber-500" data-id="element-14174" />,
  stats: {
    whales: {
      sent: 2500,
      opened: 1250,
      converted: 375,
      revenue: 11250
    },
    dolphins: {
      sent: 17000,
      opened: 6800,
      converted: 1360,
      revenue: 27200
    },
    minnows: {
      sent: 44000,
      opened: 13200,
      converted: 1760,
      revenue: 22000
    },
    'non-spenders': {
      sent: 316000,
      opened: 47400,
      converted: 2528,
      revenue: 12640
    },
    'churn-risks': {
      sent: 25000,
      opened: 10000,
      converted: 1500,
      revenue: 22500
    }
  },
  algorithmCoverage: {
    whales: 90,
    dolphins: 82,
    minnows: 68,
    'non-spenders': 35,
    'churn-risks': 92
  },
  fallbackMethod: {
    whales: 'lookalike',
    dolphins: 'lookalike',
    minnows: 'broad',
    'non-spenders': 'context',
    'churn-risks': 'lookalike'
  }
}];
// Fallback methods
const fallbackMethods = [{
  id: 'lookalike',
  label: 'Lookalike Audiences',
  icon: <Repeat size={16} className="text-indigo-500" data-id="element-14175" />
}, {
  id: 'broad',
  label: 'Broad Targeting',
  icon: <Zap size={16} className="text-amber-500" data-id="element-14176" />
}, {
  id: 'context',
  label: 'Context Fallback',
  icon: <Globe size={16} className="text-blue-500" data-id="element-14177" />
}, {
  id: 'persona',
  label: 'Persona Fallback',
  icon: <Theater size={16} className="text-pink-500" data-id="element-14178" />
}];
// Chart data for conversion rates
const conversionRateData = [{
  name: 'Whales',
  Push: campaignTypes.find(c => c.id === 'push')?.stats.whales.converted / campaignTypes.find(c => c.id === 'push')?.stats.whales.sent * 100,
  Email: campaignTypes.find(c => c.id === 'email')?.stats.whales.converted / campaignTypes.find(c => c.id === 'email')?.stats.whales.sent * 100,
  Retargeting: campaignTypes.find(c => c.id === 'retargeting')?.stats.whales.converted / campaignTypes.find(c => c.id === 'retargeting')?.stats.whales.sent * 100
}, {
  name: 'Dolphins',
  Push: campaignTypes.find(c => c.id === 'push')?.stats.dolphins.converted / campaignTypes.find(c => c.id === 'push')?.stats.dolphins.sent * 100,
  Email: campaignTypes.find(c => c.id === 'email')?.stats.dolphins.converted / campaignTypes.find(c => c.id === 'email')?.stats.dolphins.sent * 100,
  Retargeting: campaignTypes.find(c => c.id === 'retargeting')?.stats.dolphins.converted / campaignTypes.find(c => c.id === 'retargeting')?.stats.dolphins.sent * 100
}, {
  name: 'Minnows',
  Push: campaignTypes.find(c => c.id === 'push')?.stats.minnows.converted / campaignTypes.find(c => c.id === 'push')?.stats.minnows.sent * 100,
  Email: campaignTypes.find(c => c.id === 'email')?.stats.minnows.converted / campaignTypes.find(c => c.id === 'email')?.stats.minnows.sent * 100,
  Retargeting: campaignTypes.find(c => c.id === 'retargeting')?.stats.minnows.converted / campaignTypes.find(c => c.id === 'retargeting')?.stats.minnows.sent * 100
}];
// Chart data for revenue by campaign
const revenueData = [{
  name: 'Whales',
  Push: campaignTypes.find(c => c.id === 'push')?.stats.whales.revenue || 0,
  Email: campaignTypes.find(c => c.id === 'email')?.stats.whales.revenue || 0,
  Retargeting: campaignTypes.find(c => c.id === 'retargeting')?.stats.whales.revenue || 0
}, {
  name: 'Dolphins',
  Push: campaignTypes.find(c => c.id === 'push')?.stats.dolphins.revenue || 0,
  Email: campaignTypes.find(c => c.id === 'email')?.stats.dolphins.revenue || 0,
  Retargeting: campaignTypes.find(c => c.id === 'retargeting')?.stats.dolphins.revenue || 0
}, {
  name: 'Minnows',
  Push: campaignTypes.find(c => c.id === 'push')?.stats.minnows.revenue || 0,
  Email: campaignTypes.find(c => c.id === 'email')?.stats.minnows.revenue || 0,
  Retargeting: campaignTypes.find(c => c.id === 'retargeting')?.stats.minnows.revenue || 0
}];
// Re-engagement campaigns
const reEngagementCampaigns = [{
  id: 'login-streak',
  name: 'Login Streak Reminder',
  description: 'Remind users to maintain their login streak for bonus tokens',
  target: 'churn-risks',
  algorithmCoverage: 95,
  fallbackMethod: 'lookalike',
  lastSent: '2023-09-15',
  conversions: 2850,
  revenue: 42750
}, {
  id: 'new-angels',
  name: 'New Angels Announcement',
  description: 'Announce new angels to bring back inactive users',
  target: 'all',
  algorithmCoverage: 85,
  fallbackMethod: 'broad',
  lastSent: '2023-09-10',
  conversions: 5200,
  revenue: 78000
}, {
  id: 'limited-offer',
  name: 'Limited Time Offer',
  description: 'Special discount on token packs for 24 hours only',
  target: 'dolphins',
  algorithmCoverage: 90,
  fallbackMethod: 'lookalike',
  lastSent: '2023-09-08',
  conversions: 3500,
  revenue: 87500
}, {
  id: 'content-update',
  name: 'Content Update',
  description: 'Notify users of new premium content available',
  target: 'whales',
  algorithmCoverage: 92,
  fallbackMethod: 'lookalike',
  lastSent: '2023-09-05',
  conversions: 1850,
  revenue: 55500
}, {
  id: 'free-tokens',
  name: 'Free Tokens Giveaway',
  description: 'Offer free tokens to non-spenders to get them engaged',
  target: 'non-spenders',
  algorithmCoverage: 65,
  fallbackMethod: 'context',
  lastSent: '2023-09-01',
  conversions: 12500,
  revenue: 31250
}];
export const ReEngagementTools: React.FC = () => {
  const [selectedSegment, setSelectedSegment] = useState('all');
  const [campaignFallbacks, setCampaignFallbacks] = useState(campaignTypes.reduce((acc, campaign) => ({
    ...acc,
    [campaign.id]: campaign.fallbackMethod
  }), {}));
  const handleFallbackMethodChange = (campaignId: string, segment: string, method: string) => {
    setCampaignFallbacks(prev => ({
      ...prev,
      [campaignId]: {
        ...prev[campaignId],
        [segment]: method
      }
    }));
  };
  const totalRevenue = campaignTypes.reduce((sum, campaign) => {
    return sum + Object.values(campaign.stats).reduce((campaignSum, stats) => {
      return campaignSum + stats.revenue;
    }, 0);
  }, 0);
  const totalConversions = campaignTypes.reduce((sum, campaign) => {
    return sum + Object.values(campaign.stats).reduce((campaignSum, stats) => {
      return campaignSum + stats.converted;
    }, 0);
  }, 0);
  const avgOpenRate = campaignTypes.reduce((sum, campaign) => {
    const campaignOpenRate = Object.values(campaign.stats).reduce((openSum, stats) => {
      return openSum + stats.opened / stats.sent;
    }, 0) / Object.values(campaign.stats).length;
    return sum + campaignOpenRate;
  }, 0) / campaignTypes.length * 100;
  const avgConversionRate = campaignTypes.reduce((sum, campaign) => {
    const campaignConversionRate = Object.values(campaign.stats).reduce((convSum, stats) => {
      return convSum + stats.converted / stats.sent;
    }, 0) / Object.values(campaign.stats).length;
    return sum + campaignConversionRate;
  }, 0) / campaignTypes.length * 100;
  return <div data-id="element-14179">
      <div className="flex items-center justify-between mb-6" data-id="element-14180">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-14181">
          Re-Engagement Tools
        </h2>
        <div className="flex items-center gap-3" data-id="element-14182">
          <SegmentDropdown label="" options={segments} value={selectedSegment} onChange={setSelectedSegment} className="w-48" data-id="element-14183" />
          <button className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-14184">
            <Download size={16} data-id="element-14185" />
            <span data-id="element-14186">Export Data</span>
          </button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-id="element-14187">
        <StatsCard title="Total Revenue" value={`$${(totalRevenue / 1000).toFixed(1)}K`} change={12.5} changeLabel="vs last month" icon={<DollarSign size={16} className="text-green-500" data-id="element-14189" />} className="bg-gradient-to-br from-white to-green-50 dark:from-gray-800 dark:to-green-900/20" data-id="element-14188" />
        <StatsCard title="Total Conversions" value={totalConversions.toLocaleString()} change={8.2} changeLabel="vs last month" icon={<TrendingUp size={16} className="text-blue-500" data-id="element-14191" />} className="bg-gradient-to-br from-white to-blue-50 dark:from-gray-800 dark:to-blue-900/20" data-id="element-14190" />
        <StatsCard title="Avg. Open Rate" value={`${avgOpenRate.toFixed(1)}%`} change={3.7} changeLabel="vs last month" icon={<Bell size={16} className="text-amber-500" data-id="element-14193" />} data-id="element-14192" />
        <StatsCard title="Avg. Conversion Rate" value={`${avgConversionRate.toFixed(1)}%`} change={2.3} changeLabel="vs last month" icon={<BarChart size={16} className="text-purple-500" data-id="element-14195" />} data-id="element-14194" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6" data-id="element-14196">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden" data-id="element-14197">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50" data-id="element-14198">
            <h3 className="font-medium text-gray-900 dark:text-white flex items-center" data-id="element-14199">
              <BarChart size={18} className="text-blue-500 mr-2" data-id="element-14200" />
              Conversion Rate by Segment
            </h3>
          </div>
          <div className="p-6" data-id="element-14201">
            <div className="h-80" data-id="element-14202">
              <Chart title="" data={conversionRateData} type="bar" height={300} yAxisLabel="Conversion Rate (%)" data-id="element-14203" />
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden" data-id="element-14204">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50" data-id="element-14205">
            <h3 className="font-medium text-gray-900 dark:text-white flex items-center" data-id="element-14206">
              <DollarSign size={18} className="text-green-500 mr-2" data-id="element-14207" />
              Revenue by Campaign Type
            </h3>
          </div>
          <div className="p-6" data-id="element-14208">
            <div className="h-80" data-id="element-14209">
              <Chart title="" data={revenueData} type="bar" height={300} yAxisLabel="Revenue ($)" data-id="element-14210" />
            </div>
          </div>
        </div>
      </div>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden mb-6" data-id="element-14211">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50" data-id="element-14212">
          <h3 className="font-medium text-gray-900 dark:text-white" data-id="element-14213">
            Campaign Performance by Segment
          </h3>
        </div>
        <div className="overflow-x-auto" data-id="element-14214">
          <table className="w-full" data-id="element-14215">
            <thead data-id="element-14216">
              <tr className="bg-gray-50 dark:bg-gray-900/30" data-id="element-14217">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-14218">
                  Campaign Type
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-14219">
                  <div className="flex items-center justify-center" data-id="element-14220">
                    <DollarSign size={14} className="text-emerald-500 mr-1" data-id="element-14221" />
                    <span data-id="element-14222">Whales</span>
                  </div>
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-14223">
                  <div className="flex items-center justify-center" data-id="element-14224">
                    <Droplet size={14} className="text-blue-500 mr-1" data-id="element-14225" />
                    <span data-id="element-14226">Dolphins</span>
                  </div>
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-14227">
                  <div className="flex items-center justify-center" data-id="element-14228">
                    <Fish size={14} className="text-cyan-500 mr-1" data-id="element-14229" />
                    <span data-id="element-14230">Minnows</span>
                  </div>
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-14231">
                  <div className="flex items-center justify-center" data-id="element-14232">
                    <Wallet size={14} className="text-gray-500 mr-1" data-id="element-14233" />
                    <span data-id="element-14234">Non-Spenders</span>
                  </div>
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-14235">
                  <div className="flex items-center justify-center" data-id="element-14236">
                    <AlertCircle size={14} className="text-red-500 mr-1" data-id="element-14237" />
                    <span data-id="element-14238">Churn Risks</span>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-14239">
              {campaignTypes.map(campaign => <tr key={campaign.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/60" data-id="element-14240">
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-14241">
                    <div className="flex items-center" data-id="element-14242">
                      <div className="flex-shrink-0" data-id="element-14243">
                        <div className="w-8 h-8 rounded-md bg-gray-100 dark:bg-gray-700 flex items-center justify-center" data-id="element-14244">
                          {campaign.icon}
                        </div>
                      </div>
                      <div className="ml-3" data-id="element-14245">
                        <div className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-14246">
                          {campaign.name}
                        </div>
                      </div>
                    </div>
                  </td>
                  {['whales', 'dolphins', 'minnows', 'non-spenders', 'churn-risks'].map(segment => <td key={segment} className="px-6 py-4 whitespace-nowrap" data-id="element-14247">
                      <div className="flex flex-col items-center space-y-2" data-id="element-14248">
                        <div className="text-sm text-gray-700 dark:text-gray-300" data-id="element-14249">
                          <div className="flex justify-between gap-4" data-id="element-14250">
                            <span data-id="element-14251">
                              Open:{' '}
                              {(campaign.stats[segment].opened / campaign.stats[segment].sent * 100).toFixed(1)}
                              %
                            </span>
                            <span data-id="element-14252">
                              Conv:{' '}
                              {(campaign.stats[segment].converted / campaign.stats[segment].sent * 100).toFixed(1)}
                              %
                            </span>
                          </div>
                          <div className="text-sm font-medium text-green-600 dark:text-green-400" data-id="element-14253">
                            ${campaign.stats[segment].revenue.toLocaleString()}
                          </div>
                        </div>
                        <div className="flex items-center text-xs" data-id="element-14254">
                          <Brain size={12} className="text-purple-500 mr-1" data-id="element-14255" />
                          <span className="text-gray-500 dark:text-gray-400" data-id="element-14256">
                            {campaign.algorithmCoverage[segment]}%
                          </span>
                        </div>
                        <select value={campaignFallbacks[campaign.id][segment]} onChange={e => handleFallbackMethodChange(campaign.id, segment, e.target.value)} className="block w-full text-xs px-2 py-1 border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-md focus:outline-none focus:ring-purple-500 focus:border-purple-500 dark:focus:ring-purple-400 dark:focus:border-purple-400" data-id="element-14257">
                          {fallbackMethods.map(method => <option key={method.id} value={method.id} data-id="element-14258">
                              {method.label}
                            </option>)}
                        </select>
                      </div>
                    </td>)}
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden mb-6" data-id="element-14259">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50" data-id="element-14260">
          <h3 className="font-medium text-gray-900 dark:text-white flex items-center" data-id="element-14261">
            <Calendar size={18} className="text-indigo-500 mr-2" data-id="element-14262" />
            Recent Re-Engagement Campaigns
          </h3>
        </div>
        <div className="overflow-x-auto" data-id="element-14263">
          <table className="w-full" data-id="element-14264">
            <thead data-id="element-14265">
              <tr className="bg-gray-50 dark:bg-gray-900/30" data-id="element-14266">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-14267">
                  Campaign
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-14268">
                  Target Segment
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-14269">
                  Algorithm Coverage
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-14270">
                  Fallback Method
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-14271">
                  Last Sent
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-14272">
                  Conversions
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-14273">
                  Revenue
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-14274">
              {reEngagementCampaigns.map(campaign => <tr key={campaign.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/60" data-id="element-14275">
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-14276">
                    <div className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-14277">
                      {campaign.name}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400" data-id="element-14278">
                      {campaign.description}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300" data-id="element-14279">
                    {campaign.target === 'all' ? <span data-id="element-14280">All Segments</span> : <div className="flex items-center" data-id="element-14281">
                        {campaign.target === 'whales' ? <DollarSign size={14} className="text-emerald-500 mr-1" data-id="element-14282" /> : campaign.target === 'dolphins' ? <Droplet size={14} className="text-blue-500 mr-1" data-id="element-14283" /> : campaign.target === 'minnows' ? <Fish size={14} className="text-cyan-500 mr-1" data-id="element-14284" /> : campaign.target === 'non-spenders' ? <Wallet size={14} className="text-gray-500 mr-1" data-id="element-14285" /> : <AlertCircle size={14} className="text-red-500 mr-1" data-id="element-14286" />}
                        <span data-id="element-14287">
                          {campaign.target.charAt(0).toUpperCase() + campaign.target.slice(1).replace('-', ' ')}
                        </span>
                      </div>}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300" data-id="element-14288">
                    <div className="flex items-center" data-id="element-14289">
                      <div className="w-16 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden" data-id="element-14290">
                        <div className="h-full bg-purple-500 rounded-full" style={{
                      width: `${campaign.algorithmCoverage}%`
                    }} data-id="element-14291"></div>
                      </div>
                      <span className="ml-2" data-id="element-14292">
                        {campaign.algorithmCoverage}%
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300" data-id="element-14293">
                    <div className="flex items-center" data-id="element-14294">
                      {fallbackMethods.find(m => m.id === campaign.fallbackMethod)?.icon}
                      <span className="ml-2" data-id="element-14295">
                        {fallbackMethods.find(m => m.id === campaign.fallbackMethod)?.label}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300" data-id="element-14296">
                    {campaign.lastSent}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300" data-id="element-14297">
                    {campaign.conversions.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600 dark:text-green-400" data-id="element-14298">
                    ${campaign.revenue.toLocaleString()}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-14299">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden" data-id="element-14300">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50" data-id="element-14301">
            <h3 className="font-medium text-gray-900 dark:text-white flex items-center" data-id="element-14302">
              <Brain size={18} className="text-purple-500 mr-2" data-id="element-14303" />
              Algorithmic vs Fallback Messaging
            </h3>
          </div>
          <div className="p-4" data-id="element-14304">
            <div className="space-y-4" data-id="element-14305">
              <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4" data-id="element-14306">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2 flex items-center" data-id="element-14307">
                  <Brain size={16} className="text-purple-500 mr-2" data-id="element-14308" />
                  Algorithmic Message
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2" data-id="element-14309">
                  When algorithm confidence is high, messages are highly
                  personalized based on user behavior:
                </p>
                <div className="bg-white dark:bg-gray-800 p-3 rounded-md border border-gray-200 dark:border-gray-700" data-id="element-14310">
                  <div className="flex items-center mb-2" data-id="element-14311">
                    <Bell size={14} className="text-blue-500 mr-2" data-id="element-14312" />
                    <span className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-14313">
                      Push Notification Example (Whale)
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 dark:text-gray-300" data-id="element-14314">
                    "John, your favorite Angel Sophia just posted new exclusive
                    content. She mentioned you specifically in her latest
                    update!"
                  </p>
                </div>
              </div>
              <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-lg p-4" data-id="element-14315">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2 flex items-center" data-id="element-14316">
                  <Repeat size={16} className="text-indigo-500 mr-2" data-id="element-14317" />
                  Lookalike Fallback Message
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2" data-id="element-14318">
                  When algorithm confidence is medium, messages target users
                  similar to known high-value customers:
                </p>
                <div className="bg-white dark:bg-gray-800 p-3 rounded-md border border-gray-200 dark:border-gray-700" data-id="element-14319">
                  <div className="flex items-center mb-2" data-id="element-14320">
                    <Mail size={14} className="text-purple-500 mr-2" data-id="element-14321" />
                    <span className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-14322">
                      Email Example (Dolphin)
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 dark:text-gray-300" data-id="element-14323">
                    "Users like you are enjoying our new Angels. Check out
                    Sophia's exclusive content that just dropped!"
                  </p>
                </div>
              </div>
              <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4" data-id="element-14324">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2 flex items-center" data-id="element-14325">
                  <Zap size={16} className="text-amber-500 mr-2" data-id="element-14326" />
                  Broad Targeting Fallback
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2" data-id="element-14327">
                  When algorithm confidence is low, broader messages are used to
                  gather more signals:
                </p>
                <div className="bg-white dark:bg-gray-800 p-3 rounded-md border border-gray-200 dark:border-gray-700" data-id="element-14328">
                  <div className="flex items-center mb-2" data-id="element-14329">
                    <Repeat size={14} className="text-amber-500 mr-2" data-id="element-14330" />
                    <span className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-14331">
                      Retargeting Example (Non-Spender)
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 dark:text-gray-300" data-id="element-14332">
                    "Limited time offer: Get 100 free tokens when you return to
                    SinShift today!"
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden" data-id="element-14333">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50" data-id="element-14334">
            <h3 className="font-medium text-gray-900 dark:text-white flex items-center" data-id="element-14335">
              <AlertCircle size={18} className="text-red-500 mr-2" data-id="element-14336" />
              Churn Risk Recovery Strategy
            </h3>
          </div>
          <div className="p-4" data-id="element-14337">
            <div className="space-y-4" data-id="element-14338">
              <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4" data-id="element-14339">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2 flex items-center" data-id="element-14340">
                  <AlertCircle size={16} className="text-red-500 mr-2" data-id="element-14341" />
                  Churn Risk Identification
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-14342">
                  The algorithm identifies users at risk of churning based on:
                </p>
                <ul className="mt-2 space-y-1 text-sm text-gray-600 dark:text-gray-400 list-disc pl-5" data-id="element-14343">
                  <li data-id="element-14344">Declining login frequency</li>
                  <li data-id="element-14345">Reduced time spent in app</li>
                  <li data-id="element-14346">Decreased chat engagement</li>
                  <li data-id="element-14347">Lower token spend rate</li>
                  <li data-id="element-14348">Subscription cancellation signals</li>
                </ul>
              </div>
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-lg p-4" data-id="element-14349">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2" data-id="element-14350">
                  Algorithmic Recovery Sequence
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2" data-id="element-14351">
                  When a user is identified as a churn risk, the system
                  automatically initiates a recovery sequence:
                </p>
                <div className="space-y-3" data-id="element-14352">
                  <div className="flex items-start" data-id="element-14353">
                    <div className="flex items-center justify-center w-6 h-6 rounded-full bg-purple-100 dark:bg-purple-900/50 text-purple-600 dark:text-purple-400 flex-shrink-0 mt-0.5" data-id="element-14354">
                      1
                    </div>
                    <div className="ml-3" data-id="element-14355">
                      <p className="text-sm text-gray-700 dark:text-gray-300" data-id="element-14356">
                        <strong data-id="element-14357">Personalized Message</strong> from preferred
                        Angel (90% algorithm coverage)
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-14358">
                    <div className="flex items-center justify-center w-6 h-6 rounded-full bg-indigo-100 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-400 flex-shrink-0 mt-0.5" data-id="element-14359">
                      2
                    </div>
                    <div className="ml-3" data-id="element-14360">
                      <p className="text-sm text-gray-700 dark:text-gray-300" data-id="element-14361">
                        <strong data-id="element-14362">Token Bonus Offer</strong> based on previous
                        spending pattern (85% algorithm coverage)
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-14363">
                    <div className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" data-id="element-14364">
                      3
                    </div>
                    <div className="ml-3" data-id="element-14365">
                      <p className="text-sm text-gray-700 dark:text-gray-300" data-id="element-14366">
                        <strong data-id="element-14367">Exclusive Content Access</strong> aligned with
                        user preferences (80% algorithm coverage)
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-14368">
                    <div className="flex items-center justify-center w-6 h-6 rounded-full bg-amber-100 dark:bg-amber-900/50 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" data-id="element-14369">
                      4
                    </div>
                    <div className="ml-3" data-id="element-14370">
                      <p className="text-sm text-gray-700 dark:text-gray-300" data-id="element-14371">
                        <strong data-id="element-14372">Fallback: Special Offer</strong> based on
                        lookalike audiences if no response (100% fallback)
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4" data-id="element-14373">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2 flex items-center" data-id="element-14374">
                  <TrendingUp size={16} className="text-green-500 mr-2" data-id="element-14375" />
                  Recovery Performance
                </h4>
                <div className="flex justify-between items-center" data-id="element-14376">
                  <div className="text-sm text-gray-600 dark:text-gray-400" data-id="element-14377">
                    Recovery Rate:
                  </div>
                  <div className="text-sm font-medium text-green-600 dark:text-green-400" data-id="element-14378">
                    42%
                  </div>
                </div>
                <div className="flex justify-between items-center" data-id="element-14379">
                  <div className="text-sm text-gray-600 dark:text-gray-400" data-id="element-14380">
                    Avg. Revenue Recovered:
                  </div>
                  <div className="text-sm font-medium text-green-600 dark:text-green-400" data-id="element-14381">
                    $58.75 per user
                  </div>
                </div>
                <div className="flex justify-between items-center" data-id="element-14382">
                  <div className="text-sm text-gray-600 dark:text-gray-400" data-id="element-14383">
                    Algorithm vs Fallback Lift:
                  </div>
                  <div className="text-sm font-medium text-green-600 dark:text-green-400" data-id="element-14384">
                    +35%
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
};